import {
    Box,
    Container,
    Grid,
    Stack,
    Typography,
    Avatar,
    Skeleton,
    LinearProgress,
    Button,
    CircularProgress,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery, useInfiniteQuery } from "@tanstack/react-query";
import { axiosClient } from "../../data/axios";
import type { PaginatedGameSummary, PlayerProfile } from "../../types";
import { getChampionImage } from "../../utils/team";
import { MatchCard } from "../../components/games/history";
import {
    ChampionStatsTable,
    type ChampionStats,
} from "../../components/championStatsTable";

const MATCH_PAGE_SIZE = 5;

export const Route = createFileRoute("/player/$playerId")({
    component: RouteComponent,
});

const BLUE = "#0a97ab";
const RED = "#bf1e36";

const formatRole = (role: string) => {
    const map: Record<string, string> = {
        TOP: "Top",
        JUNGLE: "Jungle",
        MIDDLE: "Mid",
        BOTTOM: "Bot",
        UTILITY: "Support",
        SUPPORT: "Support",
    };
    return map[role.toUpperCase()] ?? role;
};

const labelSx = {
    color: "#6c7280",
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: 1.5,
    textTransform: "uppercase" as const,
};

const StatTile = ({ value, label }: { value: number | string; label: string }) => (
    <Box
        sx={{
            backgroundColor: "#161b22",
            border: "1px solid #2a3140",
            borderRadius: 2,
            p: 2,
            height: "100%",
        }}
    >
        <Typography
            sx={{
                color: "#e6e8ee",
                fontSize: 28,
                fontWeight: 700,
                fontFamily: "'Roboto Mono', monospace",
                lineHeight: 1,
            }}
        >
            {value}
        </Typography>
        <Typography sx={{ ...labelSx, mt: 0.75 }}>{label}</Typography>
    </Box>
);

const SideCard = ({
    side,
    color,
    games,
    wins,
}: {
    side: string;
    color: string;
    games: number;
    wins: number;
}) => {
    const pct = games > 0 ? Math.round((wins / games) * 100) : 0;
    return (
        <Box
            sx={{
                backgroundColor: "#161b22",
                border: "1px solid #2a3140",
                borderRadius: 2,
                p: 2.5,
                position: "relative",
                overflow: "hidden",
                "&::before": {
                    content: '""',
                    position: "absolute",
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: 4,
                    backgroundColor: color,
                },
            }}
        >
            <Typography sx={{ color, fontSize: 11, fontWeight: 700, letterSpacing: 1.5, mb: 1 }}>
                {side} SIDE
            </Typography>
            {games > 0 ? (
                <>
                    <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mb: 1 }}>
                        <Typography
                            sx={{
                                color: "#e6e8ee",
                                fontSize: 36,
                                fontWeight: 700,
                                fontFamily: "'Roboto Mono', monospace",
                                lineHeight: 1,
                            }}
                        >
                            {pct}%
                        </Typography>
                        <Typography sx={{ color: "#9ba1b0", fontSize: 13 }}>
                            {wins} W · {games} {games === 1 ? "game" : "games"}
                        </Typography>
                    </Stack>
                    <LinearProgress
                        variant="determinate"
                        value={pct}
                        sx={{
                            height: 4,
                            borderRadius: 2,
                            backgroundColor: "#2a3140",
                            "& .MuiLinearProgress-bar": { backgroundColor: color },
                        }}
                    />
                </>
            ) : (
                <Typography sx={{ color: "#9ba1b0", fontSize: 13 }}>No games on this side yet</Typography>
            )}
        </Box>
    );
};

const SectionTitle = ({ children }: { children: React.ReactNode }) => (
    <Typography sx={{ ...labelSx, mb: 1.5, color: "#9ba1b0" }}>{children}</Typography>
);

function RouteComponent() {
    const { playerId } = Route.useParams();
    const navigate = useNavigate();

    const { data, isLoading } = useQuery<PlayerProfile | null>({
        queryKey: ["player", playerId, "profile"],
        queryFn: () =>
            axiosClient.get(`/players/${playerId}/profile`).then((r) => r.data),
        staleTime: 5 * 60 * 1000,
    });

    const { data: championStats } = useQuery<ChampionStats[]>({
        queryKey: ["player", playerId, "champions"],
        queryFn: () =>
            axiosClient.get(`/players/${playerId}/champions`).then((r) => r.data),
        staleTime: 5 * 60 * 1000,
    });

    const {
        data: matches,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        isLoading: matchesLoading,
    } = useInfiniteQuery<PaginatedGameSummary>({
        queryKey: ["games", "summary", "byPlayer", playerId, MATCH_PAGE_SIZE],
        queryFn: ({ pageParam }) =>
            axiosClient
                .get("/games/summary", {
                    params: {
                        page: pageParam,
                        pageSize: MATCH_PAGE_SIZE,
                        playerId,
                    },
                })
                .then((r) => r.data),
        initialPageParam: 1,
        getNextPageParam: (last) => (last.hasMore ? last.page + 1 : undefined),
        staleTime: 5 * 60 * 1000,
    });

    const games = matches?.pages.flatMap((p) => p.items) ?? [];

    if (isLoading) {
        return (
            <Container maxWidth="lg" sx={{ py: 3 }}>
                <Skeleton variant="rounded" height={180} sx={{ bgcolor: "#161b22", borderRadius: 2, mb: 3 }} />
                <Grid container spacing={2}>
                    {Array.from({ length: 4 }).map((_, i) => (
                        <Grid key={i} size={{ xs: 6, md: 3 }}>
                            <Skeleton variant="rounded" height={90} sx={{ bgcolor: "#161b22", borderRadius: 2 }} />
                        </Grid>
                    ))}
                </Grid>
            </Container>
        );
    }

    if (!data) {
        return (
            <Container maxWidth="lg" sx={{ py: 3 }}>
                <Typography sx={{ color: "#9ba1b0" }}>Player not found.</Typography>
            </Container>
        );
    }

    const champ = data.mostPlayedChampion;
    const totalSpree = data.highlights.largestKillingSpree;
    const totalMulti = data.highlights.largestMultiKill;

    return (
        <Container maxWidth="lg" sx={{ py: 3 }}>
            <Button
                onClick={() => navigate({ to: "/player" })}
                startIcon={<ArrowBackIcon />}
                sx={{ color: "#9ba1b0", mb: 2, textTransform: "none" }}
            >
                All players
            </Button>

            {/* HERO */}
            <Box
                sx={{
                    position: "relative",
                    overflow: "hidden",
                    borderRadius: 2,
                    backgroundColor: "#161b22",
                    border: "1px solid #2a3140",
                    p: 3,
                    mb: 3,
                }}
            >
                {champ && (
                    <Box
                        component="img"
                        src={getChampionImage(champ.name)}
                        alt={champ.name}
                        sx={{
                            position: "absolute",
                            right: -60,
                            top: -40,
                            width: 360,
                            height: 360,
                            opacity: 0.15,
                            filter: "blur(2px)",
                            pointerEvents: "none",
                        }}
                    />
                )}
                <Stack direction="row" spacing={3} sx={{ position: "relative", alignItems: "center" }}>
                    {champ ? (
                        <Avatar
                            src={getChampionImage(champ.name)}
                            alt={champ.name}
                            sx={{ width: 112, height: 112, border: "2px solid #2a3140" }}
                        />
                    ) : (
                        <Avatar sx={{ width: 112, height: 112, bgcolor: "#2a3140" }} />
                    )}
                    <Stack spacing={0.5}>
                        <Typography
                            sx={{ color: "#ffffff", fontSize: 32, fontWeight: 700, letterSpacing: 0.5 }}
                        >
                            {data.summonerName}
                        </Typography>
                        {champ ? (
                            <Typography sx={{ color: "#9ba1b0", fontSize: 14 }}>
                                Mains{" "}
                                <Box component="span" sx={{ color: "#e6e8ee", fontWeight: 600 }}>
                                    {champ.name}
                                </Box>{" "}
                                · {champ.games}/{data.gamesPlayed} games
                            </Typography>
                        ) : (
                            <Typography sx={{ color: "#9ba1b0", fontSize: 14 }}>No games yet</Typography>
                        )}
                    </Stack>
                </Stack>
            </Box>

            {/* AT A GLANCE */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid size={{ xs: 6, md: 3 }}>
                    <StatTile value={data.gamesPlayed} label="Games" />
                </Grid>
                <Grid size={{ xs: 6, md: 3 }}>
                    <StatTile value={data.totalWins} label="Wins" />
                </Grid>
                <Grid size={{ xs: 6, md: 3 }}>
                    <StatTile value={data.totalKills} label="Total Kills" />
                </Grid>
                <Grid size={{ xs: 6, md: 3 }}>
                    <StatTile value={data.totalAssists} label="Total Assists" />
                </Grid>
            </Grid>

            {/* SIDE PERFORMANCE */}
            <SectionTitle>Performance by side</SectionTitle>
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid size={{ xs: 12, md: 6 }}>
                    <SideCard
                        side="BLUE"
                        color={BLUE}
                        games={data.sideStats.BLUE.games}
                        wins={data.sideStats.BLUE.wins}
                    />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                    <SideCard
                        side="RED"
                        color={RED}
                        games={data.sideStats.RED.games}
                        wins={data.sideStats.RED.wins}
                    />
                </Grid>
            </Grid>

            {/* TOP CHAMPIONS */}
            <SectionTitle>Top champions</SectionTitle>
            <Grid container spacing={2} sx={{ mb: 3 }}>
                {data.topChampions.map((c) => (
                    <Grid key={c.name} size={{ xs: 6, sm: 4, md: 2 }}>
                        <Stack
                            alignItems="center"
                            spacing={0.5}
                            sx={{
                                backgroundColor: "#161b22",
                                border: "1px solid #2a3140",
                                borderRadius: 2,
                                p: 2,
                            }}
                        >
                            <Avatar
                                src={getChampionImage(c.name)}
                                alt={c.name}
                                sx={{ width: 56, height: 56, border: "2px solid #2a3140", mb: 0.5 }}
                            />
                            <Typography sx={{ color: "#e6e8ee", fontSize: 13, fontWeight: 600 }} noWrap>
                                {c.name}
                            </Typography>
                            <Typography sx={{ color: "#9ba1b0", fontSize: 11 }}>
                                {c.games} {c.games === 1 ? "game" : "games"}
                            </Typography>
                        </Stack>
                    </Grid>
                ))}
            </Grid>

            {/* ALL CHAMPIONS PLAYED */}
            {championStats && championStats.length > 0 && (
                <Box sx={{ mb: 3 }}>
                    <SectionTitle>All champions played</SectionTitle>
                    <ChampionStatsTable data={championStats} maxHeight={400} />
                </Box>
            )}

            {/* HIGHLIGHTS + ROLES */}
            <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 7 }}>
                    <SectionTitle>Highlights</SectionTitle>
                    <Grid container spacing={2}>
                        <Grid size={{ xs: 6, md: 4 }}>
                            <StatTile value={totalSpree || "—"} label="Longest spree" />
                        </Grid>
                        <Grid size={{ xs: 6, md: 4 }}>
                            <StatTile value={totalMulti || "—"} label="Best multi-kill" />
                        </Grid>
                        <Grid size={{ xs: 6, md: 4 }}>
                            <StatTile value={data.highlights.doubleKills} label="Doublekills" />
                        </Grid>
                        <Grid size={{ xs: 6, md: 4 }}>
                            <StatTile value={data.highlights.tripleKills} label="Triplekills" />
                        </Grid>
                        <Grid size={{ xs: 6, md: 4 }}>
                            <StatTile value={data.highlights.quadraKills} label="Quadrakills" />
                        </Grid>
                        <Grid size={{ xs: 6, md: 4 }}>
                            <StatTile value={data.highlights.pentaKills} label="Pentakills" />
                        </Grid>
                    </Grid>
                </Grid>
                <Grid size={{ xs: 12, md: 5 }}>
                    <SectionTitle>Roles played</SectionTitle>
                    <Box
                        sx={{
                            backgroundColor: "#161b22",
                            border: "1px solid #2a3140",
                            borderRadius: 2,
                            p: 2,
                        }}
                    >
                        <Stack spacing={1.5}>
                            {data.roles.map((r) => {
                                const pct =
                                    data.gamesPlayed > 0
                                        ? (r.games / data.gamesPlayed) * 100
                                        : 0;
                                return (
                                    <Box key={r.role}>
                                        <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                                            <Typography sx={{ color: "#e6e8ee", fontSize: 13 }}>
                                                {formatRole(r.role)}
                                            </Typography>
                                            <Typography
                                                sx={{
                                                    color: "#9ba1b0",
                                                    fontSize: 12,
                                                    fontFamily: "'Roboto Mono', monospace",
                                                }}
                                            >
                                                {r.games}
                                            </Typography>
                                        </Stack>
                                        <LinearProgress
                                            variant="determinate"
                                            value={pct}
                                            sx={{
                                                height: 4,
                                                borderRadius: 2,
                                                backgroundColor: "#2a3140",
                                                "& .MuiLinearProgress-bar": { backgroundColor: "#0a97ab" },
                                            }}
                                        />
                                    </Box>
                                );
                            })}
                            {data.roles.length === 0 && (
                                <Typography sx={{ color: "#9ba1b0", fontSize: 13 }}>
                                    No role data yet
                                </Typography>
                            )}
                        </Stack>
                    </Box>
                </Grid>
            </Grid>

            {/* MATCH HISTORY */}
            <Box sx={{ mt: 4 }}>
                <SectionTitle>Match history</SectionTitle>
                {matchesLoading ? (
                    <Stack spacing={2}>
                        {Array.from({ length: 3 }).map((_, i) => (
                            <Skeleton
                                key={i}
                                variant="rounded"
                                height={84}
                                sx={{ bgcolor: "#161b22", borderRadius: 2 }}
                            />
                        ))}
                    </Stack>
                ) : games.length === 0 ? (
                    <Typography sx={{ color: "#9ba1b0", fontSize: 13 }}>
                        No matches yet.
                    </Typography>
                ) : (
                    <>
                        <Stack spacing={2}>
                            {games.map((game) => (
                                <MatchCard
                                    key={game.id}
                                    summary={game}
                                    highlightPlayerId={playerId}
                                />
                            ))}
                        </Stack>
                        {hasNextPage && (
                            <Box sx={{ mt: 3, display: "flex", justifyContent: "center" }}>
                                <Button
                                    variant="outlined"
                                    onClick={() => fetchNextPage()}
                                    disabled={isFetchingNextPage}
                                    sx={{ minWidth: 200 }}
                                >
                                    {isFetchingNextPage ? (
                                        <CircularProgress size={20} sx={{ color: "#9ba1b0" }} />
                                    ) : (
                                        "Show more"
                                    )}
                                </Button>
                            </Box>
                        )}
                    </>
                )}
            </Box>
        </Container>
    );
}
